import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Clock, ChevronRight } from "lucide-react";
import { BlogLayout } from "@/components/BlogLayout";
import { getPost, getRelated } from "@/data/blogPosts";
import { useSeo, SITE_URL } from "@/lib/seo";

// Render **bold** and *italic* inside a plain-text string.
function Inline({ text }) {
  const boldParts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {boldParts.map((part, i) => {
        if (part.startsWith("**") && part.endsWith("**")) {
          return <strong key={i} className="font-semibold text-brand-navy">{part.slice(2, -2)}</strong>;
        }
        const italicParts = part.split(/(\*[^*]+\*)/g);
        return (
          <React.Fragment key={i}>
            {italicParts.map((seg, j) =>
              seg.length > 2 && seg.startsWith("*") && seg.endsWith("*") ? (
                <em key={j} className="italic">{seg.slice(1, -1)}</em>
              ) : (
                <React.Fragment key={j}>{seg}</React.Fragment>
              )
            )}
          </React.Fragment>
        );
      })}
    </>
  );
}

function Block({ block }) {
  switch (block.type) {
    case "h2":
      return (
        <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-brand-navy mt-12 mb-4 scroll-mt-24">
          {block.text}
        </h2>
      );
    case "p":
      return <p className="text-[17px] leading-[1.75] text-stone mb-5"><Inline text={block.text} /></p>;
    case "ul":
      return (
        <ul className="list-disc pl-5 space-y-2.5 mb-6 text-[17px] leading-relaxed text-stone marker:text-brand-teal">
          {block.items.map((it, i) => <li key={i}><Inline text={it} /></li>)}
        </ul>
      );
    case "ol":
      return (
        <ol className="list-decimal pl-5 space-y-2.5 mb-6 text-[17px] leading-relaxed text-stone marker:text-brand-teal-dark marker:font-semibold">
          {block.items.map((it, i) => <li key={i} className="pl-1"><Inline text={it} /></li>)}
        </ol>
      );
    case "verse":
      return (
        <blockquote className="my-8 border-l-2 border-gold/60 pl-6 py-1">
          <p className="font-scripture italic text-xl sm:text-2xl leading-relaxed text-brand-navy">"{block.text}"</p>
          <cite className="not-italic text-sm text-gold font-semibold tracking-wide mt-2 block">— {block.ref}</cite>
        </blockquote>
      );
    case "callout":
      return (
        <div className="my-8 rounded-2xl bg-brand-teal/10 border border-brand-teal/20 p-6">
          <p className="text-[16px] leading-relaxed text-brand-navy"><Inline text={block.text} /></p>
        </div>
      );
    case "cta":
      return (
        <div className="my-10 rounded-3xl bg-brand-navy text-brand-ivory p-7 sm:p-9">
          <p className="text-brand-ivory-muted leading-relaxed mb-5">{block.text}</p>
          <Link
            to={block.to}
            data-testid="blog-inline-cta"
            className="inline-flex items-center gap-2 rounded-full bg-brand-teal text-brand-navy px-6 py-3 font-semibold hover:bg-brand-teal/90 transition-colors"
          >
            {block.label} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      );
    default:
      return null;
  }
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const post = getPost(slug);

  useSeo({
    title: post ? `${post.metaTitle || post.title}` : "Article",
    description: post?.description,
    path: post ? `/blog/${post.slug}` : "/blog",
    type: "article",
    image: post?.hero,
    jsonLd: post && {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Article",
          headline: post.title,
          description: post.description,
          image: post.hero,
          datePublished: post.date,
          dateModified: post.updated,
          author: { "@type": "Organization", name: "Not By Will" },
          publisher: {
            "@type": "Organization",
            name: "Not By Will",
            logo: { "@type": "ImageObject", url: `${SITE_URL}/nbw-logo.png` },
          },
          mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}/blog/${post.slug}` },
          keywords: (post.keywords || []).join(", "),
          articleSection: post.category,
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
            { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog` },
            { "@type": "ListItem", position: 3, name: post.title, item: `${SITE_URL}/blog/${post.slug}` },
          ],
        },
        ...(post.faqs && post.faqs.length
          ? [{
              "@type": "FAQPage",
              mainEntity: post.faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            }]
          : []),
      ],
    },
  });

  if (!post) return <Navigate to="/blog" replace />;

  const related = getRelated(post.slug, 2);
  const prettyDate = new Date(post.date).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  return (
    <BlogLayout testid="blog-post-page">
      <article className="px-6 sm:px-10 pb-8">
        <div className="max-w-2xl mx-auto">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-1.5 text-sm text-stone pt-10 mb-6" aria-label="Breadcrumb">
            <Link to="/blog" className="hover:text-brand-navy transition-colors" data-testid="blog-breadcrumb-blog">Blog</Link>
            <ChevronRight className="w-3.5 h-3.5 text-stone/50" />
            <span className="text-brand-teal-dark font-medium">{post.category}</span>
          </nav>

          {/* Title */}
          <div className="flex items-center gap-3 text-xs text-stone mb-4">
            <span className="rounded-full bg-brand-teal/10 text-brand-teal-dark px-2.5 py-1 font-semibold">{post.category}</span>
            <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {post.readMin} min read</span>
            <span>·</span>
            <time dateTime={post.date}>{prettyDate}</time>
          </div>
          <h1 data-testid="blog-post-title" className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.1] text-brand-navy">
            {post.title}
          </h1>
          <p className="text-lg text-stone mt-5 leading-relaxed">{post.excerpt}</p>
        </div>

        {/* Hero */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto mt-8 rounded-3xl overflow-hidden aspect-[16/9]"
        >
          <img src={post.hero} alt={post.heroAlt} className="w-full h-full object-cover" />
        </motion.div>

        {/* Body */}
        <div className="max-w-2xl mx-auto mt-10">
          {post.body.map((block, i) => <Block key={i} block={block} />)}
        </div>

        {/* FAQ */}
        {post.faqs?.length > 0 && (
          <div className="max-w-2xl mx-auto mt-14 pt-10 border-t border-black/5">
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-brand-navy mb-6">Frequently asked</h2>
            <div className="space-y-6">
              {post.faqs.map((f, i) => (
                <div key={i} data-testid={`blog-faq-${i}`}>
                  <h3 className="font-heading text-lg font-semibold text-brand-navy mb-1.5">{f.q}</h3>
                  <p className="text-stone leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="px-6 sm:px-10 py-14 bg-sage/50 border-t border-black/5">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-7">
              <h2 className="font-heading text-2xl font-semibold tracking-tight text-brand-navy">Keep reading</h2>
              <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-brand-teal-dark font-semibold hover:underline">
                All articles <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              {related.map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-related-${p.slug}`}
                  className="group flex gap-4 items-center rounded-2xl border border-black/5 hover:border-brand-teal/40 transition-colors p-3 bg-white"
                >
                  <img src={p.hero} alt={p.heroAlt} loading="lazy" className="w-24 h-24 rounded-xl object-cover shrink-0" />
                  <div>
                    <p className="text-xs text-brand-teal-dark font-semibold mb-1">{p.category}</p>
                    <h3 className="font-heading text-base font-semibold leading-snug text-brand-navy group-hover:text-brand-teal-dark transition-colors">{p.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </BlogLayout>
  );
}
